import type { SessionWithEntity } from "@/utils/multi-session";
import type { ClassificationType, EntityData } from "@/types/entity";
import type { RiskLevel } from "@/components/ui/risk-level-badge/RiskLevelBadge";
import type { GuideStepStatus } from "@/types/guide-step";
import type { MaturityStatus } from "@/utils/guideProgressCalculator";
import { generateSessionDisplayName } from "@/utils/multi-session";
import { getSessionActions, type SessionActions } from "@/utils/sessionActions";

export type PortfolioAlert = {
  label: string;
  date: string;
  days: number;
};

export type PortfolioStepKey =
  | "notificare"
  | "analiza-risc"
  | "maturitate"
  | "remediere";

export type PortfolioStepStatus =
  | "Finalizat"
  | "În curs"
  | "Neînceput"
  | "Blocat"
  | "loading";

export function mapDashboardToPortfolioStatus(
  status: GuideStepStatus
): PortfolioStepStatus {
  switch (status) {
    case "completed":
      return "Finalizat";
    case "in_progress":
      return "În curs";
    case "not_started":
    case "not_applicable":
      return "Neînceput";
  }
}

export function mapMaturityToPortfolioStatus(
  status: MaturityStatus
): PortfolioStepStatus {
  switch (status) {
    case "finalizat":
      return "Finalizat";
    case "in-lucru":
      return "În curs";
    case "neinceput":
      return "Neînceput";
    case "blocat":
      return "Blocat";
  }
}

export type PortfolioStep = {
  key: PortfolioStepKey;
  label: string;
  status: PortfolioStepStatus;
};

export type PortfolioCardData = {
  id: string;
  session: SessionWithEntity;
  displayName: string;
  initials: string;
  cui: string | null;
  sizeLabel: string | null;
  classification: ClassificationType | null | undefined;
  isArchived: boolean;
  steps: PortfolioStep[];
  primarySector: string | null;
  secondarySectorCount: number;
  allSectors: string[];
  employees: number | null;
  revenue: number | null;
  isNew: boolean;
  actions: SessionActions;
  lastActivityRelative: string;
  credits: number;
  alerts: PortfolioAlert[];
  riskLevel: RiskLevel | null;
  riskScore: number | null;
  riskDetail: string | null;
  maturityScore: number | null;
  maturityTarget: number | null;
  maturityDetail: string | null;
};

const SIZE_LABELS: Record<string, string> = {
  MICĂ: "Mică",
  MIJLOCIE: "Mijlocie",
  MARE: "Mare",
};

const NEW_SESSION_WINDOW_DAYS = 7;
const REMEDIERE_PREREQ_STEP = 4;

const STEP_DEFS: Array<{
  key: PortfolioStepKey;
  label: string;
  wizardStep: number;
}> = [
  { key: "notificare", label: "Notificare", wizardStep: 2 },
  { key: "analiza-risc", label: "Analiză Risc", wizardStep: 3 },
  { key: "maturitate", label: "Maturitate", wizardStep: 4 },
  { key: "remediere", label: "Remediere", wizardStep: 5 },
];

export function getCompanyInitials(name: string): string {
  if (!name) return "?";
  const words = name
    .split(/\s+/)
    .filter((w) => w.length > 1 && /[a-zA-Z0-9]/.test(w[0]));
  if (words.length === 0) {
    return name.charAt(0).toUpperCase();
  }
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export function getSizeLabel(
  dimensiune: string | null | undefined
): string | null {
  if (!dimensiune) return null;
  return SIZE_LABELS[dimensiune] ?? dimensiune;
}

function remediereStatus(currentStep: number): PortfolioStepStatus {
  if (currentStep < REMEDIERE_PREREQ_STEP) return "Blocat";
  if (currentStep > 5) return "Finalizat";
  if (currentStep === 5) return "În curs";
  return "Neînceput";
}

export function getPortfolioSteps(currentStep: number): PortfolioStep[] {
  return STEP_DEFS.map((def) => ({
    key: def.key,
    label: def.label,
    status:
      def.key === "remediere"
        ? remediereStatus(currentStep)
        : ("loading" as PortfolioStepStatus),
  }));
}

function getPrimarySector(entity: EntityData | undefined): string | null {
  const principal = entity?.caen_principal;
  if (principal && typeof principal === "object" && "cod" in principal) {
    return principal.cod ?? null;
  }
  if (typeof principal === "string") return principal;
  const services = entity?.selected_services;
  if (services && services.length > 0) {
    return services[0].sector_name ?? null;
  }
  return null;
}

function getAllSectors(entity: EntityData | undefined): string[] {
  const sectors: string[] = [];
  const primary = getPrimarySector(entity);
  if (primary) sectors.push(primary);
  const secondary = entity?.caen_secundar;
  if (Array.isArray(secondary)) {
    for (const s of secondary) {
      if (typeof s === "string" && s && !sectors.includes(s)) sectors.push(s);
    }
  }
  const services = entity?.selected_services;
  if (services && services.length > 0) {
    for (const svc of services) {
      const name = svc?.sector_name;
      if (name && !sectors.includes(name)) sectors.push(name);
    }
  }
  return sectors;
}

export function isNewSession(
  createdAt: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return false;
  const ageMs = now.getTime() - created.getTime();
  const dayMs = 86_400_000;
  return ageMs >= 0 && ageMs < NEW_SESSION_WINDOW_DAYS * dayMs;
}

export function formatRelativeRo(
  iso: string | null | undefined,
  now: Date = new Date()
): string {
  if (!iso) return "—";
  const ts = new Date(iso);
  if (Number.isNaN(ts.getTime())) return "—";
  const diffMs = now.getTime() - ts.getTime();
  if (diffMs < 0) return "acum câteva secunde";
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "acum câteva secunde";
  if (minutes < 60) {
    return `acum ${minutes} ${minutes === 1 ? "minut" : "minute"}`;
  }
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) {
    return `acum ${hours} ${hours === 1 ? "oră" : "ore"}`;
  }
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 7) {
    return `acum ${days} ${days === 1 ? "zi" : "zile"}`;
  }
  const weeks = Math.floor(days / 7);
  return `acum ${weeks} săpt.`;
}

export function toPortfolioCardData(
  session: SessionWithEntity,
  index: number,
  now: Date = new Date()
): PortfolioCardData {
  const entity = session.entity_data;
  const displayName =
    entity?.nume_entitate || generateSessionDisplayName(session, index);
  const classification = entity?.nis_classification?.classificationV2 as
    | ClassificationType
    | null
    | undefined;
  const allSectors = getAllSectors(entity);
  const primarySector = allSectors[0] ?? null;
  const secondarySectorCount = Math.max(0, allSectors.length - 1);

  return {
    id: session.id,
    session,
    displayName,
    initials: getCompanyInitials(displayName),
    cui: entity?.cui ?? null,
    sizeLabel: getSizeLabel(entity?.dimensiune ?? entity?.entity_size ?? null),
    classification: classification ?? null,
    isArchived: !!session.archived_at,
    steps: getPortfolioSteps(session.current_step ?? 0),
    primarySector,
    secondarySectorCount,
    allSectors,
    employees:
      typeof entity?.nr_salariati === "number" ? entity.nr_salariati : null,
    revenue:
      typeof entity?.cifra_afaceri === "number" ? entity.cifra_afaceri : null,
    isNew: isNewSession(session.created_at, now),
    actions: getSessionActions(session),
    lastActivityRelative: formatRelativeRo(session.updated_at, now),
    credits: 0,
    alerts: [],
    riskLevel: null,
    riskScore: null,
    riskDetail: null,
    maturityScore: null,
    maturityTarget: null,
    maturityDetail: null,
  };
}
